import React, { useState } from "react";
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { changePassword } from "../../api/settingApi";

const ChangePassword = () => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const isValidPassword = (password) => {
    const regex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,25}$/;
    return regex.test(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }

    if (!isValidPassword(newPassword)) {
      setError("Password must be 8-25 characters long, contain only letters and numbers, and include at least one letter and one number.");
      return;
    }

    setLoading(true);

    try {
      const response = await changePassword({
        currentPassword,
        newPassword
      });
      if (response && response.status === 200) {
        setSuccess("Password changed successfully!");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setTimeout(() => {
          localStorage.clear();
          navigate('/');
        }, 3000);
      } else {
        setError(response.message);
      }
    } catch (err) {
      if (err.response && err.response.data) {
        setError(err.response.data.message || "Something went wrong.");
      } else {
        setError("Failed to change password.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-blue-100">
      <form
        onSubmit={handleSubmit}
        className="bg-white p-10 rounded-lg shadow-lg w-full max-w-md border-t-8 border-green-500"
      >
        <h2 className="text-3xl font-extrabold mb-6 text-blue-800 text-center">
          Change Password
        </h2>

        {error && (
          <div className="mb-4 bg-red-600 text-white border border-red-700 p-3 rounded shadow-md flex items-start space-x-2">
            <span className="font-bold">⚠️</span>
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="mb-4 text-green-700 bg-green-100 border border-green-300 p-3 rounded">
            {success}
          </div>
        )}

        <div className="mb-4 relative">
          <label className="block text-blue-800 mb-2 font-medium" htmlFor="currentPassword">
            Current Password
          </label>
          <input
            id="currentPassword"
            type={showCurrent ? "text" : "password"}
            className="w-full px-4 py-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
          <button
            type="button"
            className="absolute right-3 top-9 text-blue-500 hover:text-green-600"
            onClick={() => setShowCurrent(!showCurrent)}
          >
            <FontAwesomeIcon icon={showCurrent ? faEyeSlash : faEye} />
          </button>
        </div>

        <div className="mb-4 relative">
          <label className="block text-blue-800 mb-2 font-medium" htmlFor="newPassword">
            New Password
          </label>
          <input
            id="newPassword"
            type={showNew ? "text" : "password"}
            className="w-full px-4 py-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            autoComplete="new-password"
          />
          <button
            type="button"
            className="absolute right-3 top-9 text-blue-500 hover:text-green-600"
            onClick={() => setShowNew(!showNew)}
          >
            <FontAwesomeIcon icon={showNew ? faEyeSlash : faEye} />
          </button>
        </div>

        <div className="mb-6 relative">
          <label className="block text-blue-800 mb-2 font-medium" htmlFor="confirmPassword">
            Confirm New Password
          </label>
          <input
            id="confirmPassword"
            type={showConfirm ? "text" : "password"}
            className="w-full px-4 py-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            autoComplete="new-password"
          />
          <button
            type="button"
            className="absolute right-3 top-9 text-blue-500 hover:text-green-600"
            onClick={() => setShowConfirm(!showConfirm)}
          >
            <FontAwesomeIcon icon={showConfirm ? faEyeSlash : faEye} />
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-green-500 text-white font-semibold py-3 rounded hover:bg-green-600 transition disabled:opacity-50"
        >
          {loading ? "Changing..." : "Change Password"}
        </button>
      </form>
    </div>
  );
};

export default ChangePassword;
